/** @jsxImportSource theme-ui @jsxRuntime classic */
import { getWindow } from '@hzdg/dom-utils';
import {
  Box,
  Button,
  getThemeByName,
  ThemeProvider,
  TishmanJSX,
} from '@tishman/components';
import { Link, ThemeUIStyleObject } from 'theme-ui';
import * as Icons from '@tishman/icons';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';

import {
  addLocaleToUrlPathname,
  formatLocale,
  getLocaleCountry,
  getLocaleLanguage,
  isLocale,
  Locale,
  setLocalStoragePreferredLocale,
  usePageContext,
  useRedirectToPreferredLocalePage,
} from '../../utils';
import localizedPathnames from '../../../translations/pathnames';

export const LocaleSwitcher = (
  props: TishmanJSX.IntrinsicElements['div'] & {
    onClose?: () => void;
  },
): React.JSX.Element | null => {
  useRedirectToPreferredLocalePage();
  const { englishPathname, locale } = usePageContext<{
    englishPathname?: string;
    locale?: Locale;
  }>();
  const window = getWindow();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const spring = useSpring(isOpen ? { maxHeight: 220 } : { maxHeight: 0 });

  // clickaway listener
  useLayoutEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!buttonRef.current || !popoverRef.current) return;
      if (!(e.target instanceof Node)) return;

      if (
        !buttonRef.current.contains(e.target) &&
        !popoverRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    window?.addEventListener('click', handleClick);
    return () => window?.removeEventListener('click', handleClick);
  }, [window]);

  // If there is no locale set, the LocaleSwitcher will not show
  if (!isLocale(locale)) return null;

  // This uses the translated localeRoutes from TransPerfect/OneLink
  // If the current decoded pathname of the window's location does not match any of the values in the localeRoutes object,
  // the LocaleSwitcher will not show
  if (
    !Object.keys(localizedPathnames).includes(decodeURI(englishPathname || ''))
  ) {
    return null;
  }

  const buttonClick = () => {
    if (!isOpen) props.onClose?.(); // Closes other overlays on locale open
    setIsOpen((t) => !t);
  };

  return (
    <Box
      sx={{
        ...(props.sx as ThemeUIStyleObject),
        position: 'relative',
        px: ['0px', '16px'],
      }}
    >
      <Button
        aria-expanded={isOpen}
        onClick={buttonClick}
        ref={buttonRef}
        sx={{
          'lineHeight': '1',
          'px': 0,
          '&::after': {
            transform: 'scaleX(1)',
          },
        }}
        variant="iconUnderline"
      >
        <Box
          as="span"
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: 1,
            justifyContent: 'center',
          }}
        >
          {getLocaleLanguage(locale)}
          <Icons.DropArrow width={12} />
        </Box>
      </Button>

      <ThemeProvider theme={getThemeByName('Rock Center Black')}>
        <Box
          sx={{
            bg: 'background',
            color: 'text',
            minWidth: 180,
            overflow: 'auto',
            position: 'absolute',
            top: '100%',
            right: '-100%',
            zIndex: 'overlay',
          }}
        >
          <animated.div ref={popoverRef} style={spring}>
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                p: 3,
              }}
            >
              {Object.values(Locale).map((locale) => (
                <Link
                  key={locale}
                  onClick={() => {
                    setIsOpen(false);
                    setLocalStoragePreferredLocale(locale);
                    if (window) {
                      window.location.href = addLocaleToUrlPathname(
                        localizedPathnames[englishPathname as string]?.[
                          locale
                        ] ?? '',
                        locale,
                      );
                    }
                  }}
                  sx={{
                    'fontSize': 2,
                    'fontWeight': 500,
                    'letterSpacing': '1px',
                    'cursor': 'pointer',
                    'py': 2,
                    '&:first-of-type': {
                      pt: 0,
                    },
                    '&:last-of-type': {
                      pb: 0,
                    },
                    '::after': {
                      bottom: 0,
                    },
                  }}
                  variant="nav"
                >
                  {(() => {
                    let formattedLocale = formatLocale(locale, {
                      languageDisplay: 'standard',
                      type: 'language',
                    });

                    if (
                      locale === Locale.EN_US ||
                      locale === Locale.ES_US ||
                      locale === Locale.KO_KR ||
                      locale === Locale.JA_JP ||
                      getLocaleCountry(locale).toLowerCase() ===
                        getLocaleLanguage(locale).toLowerCase()
                    ) {
                      formattedLocale = formattedLocale.replace(/ ?\(.+$/, '');
                    }

                    formattedLocale = formattedLocale.replace(/^\p{L}/u, (m) =>
                      m.toLocaleUpperCase(),
                    );

                    return formattedLocale;
                  })()}
                </Link>
              ))}
            </Box>
          </animated.div>
        </Box>
      </ThemeProvider>
    </Box>
  );
};
